import { useQuery } from 'react-query'

import { useLogger, useTenantMappings } from '@vp/ubik-context'

import { STALE_TIME_10_MINUTES_IN_MS } from './constants'
import { getMinimumOrderQuantity } from '../../clients/MerchantDataOrchestrationClient'
import { useAppState } from '../../components/root/contexts/AppContext'
import { useAuthorization } from '../../components/root/contexts/AuthorizationProvider'
import { useProject } from '../../components/root/contexts/ProjectContext'

export interface IUseCrossSizeMoqResult {
  /**
   * The cross-size MOQ to use: the value evaluated by Merchant Data Orchestration (MDO) when
   * set, otherwise the existing quantity-constraint-based `minimumOrderQuantity` from the
   * project as a fallback.
   */
  crossSizeMoq: number
  isLoading: boolean
}

/**
 * Resolves the cross-size minimum order quantity for the current project, preferring the value
 * evaluated by Merchant Data Orchestration (MDO) and falling back to the existing
 * quantity-constraint-based `minimumOrderQuantity` when MDO has no matching rule or the request
 * fails.
 *
 * MOQ is only evaluated via MDO for sized goods (apparel); hard goods always resolve to the
 * existing quantity-constraint-based `minimumOrderQuantity` without calling MDO.
 *
 * This is the single source of truth for cross-size MOQ. Once all products are migrated to MDO,
 * the fallback to `minimumOrderQuantity` can be removed here without changing any call sites.
 */
export default function useCrossSizeMoq(): IUseCrossSizeMoqResult {
  const { productKey, productVersion, lowestPriceAttributes, minimumOrderQuantity: fallbackMoq, isSizedGood } = useProject()
  const { country } = useAppState()
  const { authorizationHeader } = useAuthorization()
  const { pim: merchantId } = useTenantMappings()
  const logger = useLogger()

  const { data: mdoMinimumOrderQuantity, isLoading } = useQuery<number | null>(
    ['crossSizeMoq', merchantId, productKey, productVersion, country, lowestPriceAttributes],
    async () => await getMinimumOrderQuantity(merchantId, productKey, productVersion, country, lowestPriceAttributes, authorizationHeader!, logger),
    {
      enabled: isSizedGood && !!merchantId && !!productKey && !!productVersion && !!country && !!authorizationHeader,
      staleTime: STALE_TIME_10_MINUTES_IN_MS,
    },
  )

  return {
    crossSizeMoq: mdoMinimumOrderQuantity ?? fallbackMoq,
    isLoading,
  }
}
